/**
 * A pending question answered by taps, wherever it is drawn: the composer
 * strip (RFC 0304 3h) and the World view's lines (RFC 0336 S10).
 *
 * One copy of the state and the writes, so the two surfaces cannot drift on
 * what a tap means. The taps are held here, outside `QuestionTaps`, because
 * that child unmounts when its surface folds.
 *
 * ONE ANSWER PER QUESTION. Once the last tap has sent the answers (or Skip has
 * declined), further taps and Skip do nothing: the session's header keeps the
 * question until the answer syncs back, and a second tap in that gap would
 * write a second answer to a question already answered. A write that FAILS
 * lets go, so the question can be answered again.
 */

import type { QuestionBody } from '@mod/core/model'
import { answerInteraction, dismissInteraction } from '@mod/core/run'
import { useCallback, useRef, useState } from 'react'
import { useTabRepoBoot } from '../../services/world/repo/TabRepoProvider'
import { NO_TAPS, type StripTaps, stripTap } from './stripQuestion'

export interface PendingQuestion {
  interactionId: string
  body: QuestionBody
}

export function useQuestionTaps(
  sessionId: string | undefined,
  projectId: string | undefined,
  question: PendingQuestion | undefined,
): {
  taps: StripTaps
  /** An option value, or null for the Next/Send button. */
  onTap: (value: string | null) => void
  onSkip: () => void
  /** The answer (or the decline) has been sent for this question. */
  sent: boolean
} {
  const boot = useTabRepoBoot()
  const [held, setHeld] = useState<{ id: string; taps: StripTaps } | null>(null)
  const [sentFor, setSentFor] = useState<string | null>(null)
  const id = question?.interactionId
  const taps = question && held && held.id === id ? held.taps : NO_TAPS
  const sent = id !== undefined && sentFor === id
  // IN FLIGHT, synchronously: two taps inside one render would both see
  // `sent` false. Released when the write fails, so a failure can be retried
  // rather than locking the question out for as long as this is mounted.
  const inFlight = useRef<string | null>(null)

  const send = useCallback(
    (interactionId: string, write: () => Promise<unknown>) => {
      if (inFlight.current === interactionId) return
      inFlight.current = interactionId
      setSentFor(interactionId)
      const letGo = () => {
        if (inFlight.current === interactionId) inFlight.current = null
        setSentFor((prev) => (prev === interactionId ? null : prev))
      }
      // Called now, and guarded either way: a write that throws before it
      // returns a promise, or returns none, lets go the same as one that
      // rejects.
      try {
        Promise.resolve(write()).catch(letGo)
      } catch {
        letGo()
      }
    },
    [],
  )

  const onTap = useCallback(
    (value: string | null) => {
      if (!question || !sessionId || sent) return
      if (inFlight.current === question.interactionId) return
      const next = stripTap(question.body, taps, value)
      setHeld({ id: question.interactionId, taps: next.taps })
      const answers = next.answers
      if (!answers) return
      send(question.interactionId, () =>
        answerInteraction(
          boot.repo,
          sessionId,
          question.interactionId,
          answers,
          projectId ? { projectId } : {},
        ),
      )
    },
    [boot.repo, projectId, question, sessionId, taps, sent, send],
  )
  const onSkip = useCallback(() => {
    if (!question || !sessionId || sent) return
    send(question.interactionId, () =>
      dismissInteraction(
        boot.repo,
        sessionId,
        question.interactionId,
        projectId ? { projectId } : {},
      ),
    )
  }, [boot.repo, projectId, question, sessionId, sent, send])

  return { taps, onTap, onSkip, sent }
}
